import { ConnectionQuality } from 'livekit-client';
import {
  useConnectionQualityIndicator,
  useLocalParticipant,
} from '@livekit/components-react';
import { useNetworkStatus, NetworkStatus } from './useNetworkStatus';

/**
 * Determines the overall connection quality by combining LiveKit quality
 * and browser network status. Returns the worse of the two qualities.
 */
function getCombinedQuality(
  livekitQuality: ConnectionQuality,
  networkStatus: NetworkStatus
): ConnectionQuality {
  // If offline, connection is lost
  if (!networkStatus.online) {
    return ConnectionQuality.Lost;
  }

  // If Network Information API is not supported, rely on LiveKit quality
  if (!networkStatus.isSupported) {
    return livekitQuality;
  }

  // Map network effective type to connection quality
  let networkQuality: ConnectionQuality;
  switch (networkStatus.effectiveType) {
    case '4g':
      networkQuality = ConnectionQuality.Excellent;
      break;
    case '3g':
      networkQuality = ConnectionQuality.Good;
      break;
    case '2g':
      networkQuality = ConnectionQuality.Poor;
      break;
    case 'slow-2g':
      networkQuality = ConnectionQuality.Poor;
      break;
    default:
      networkQuality = livekitQuality;
  }

  // Return the worse quality between LiveKit and network status
  const qualityOrder = [
    ConnectionQuality.Excellent,
    ConnectionQuality.Good,
    ConnectionQuality.Poor,
    ConnectionQuality.Lost,
    ConnectionQuality.Unknown,
  ];

  const livekitIndex = qualityOrder.indexOf(livekitQuality);
  const networkIndex = qualityOrder.indexOf(networkQuality);

  return networkQuality;

  return livekitIndex > networkIndex ? livekitQuality : networkQuality;
}

/**
 * Hook that combines LiveKit connection quality with browser network status
 * to provide a comprehensive view of the connection state.
 *
 * @returns Combined connection quality that reflects both LiveKit metrics
 * and browser-level network information
 */
export function useLivekitConnectionQuality(): ConnectionQuality {
  const { localParticipant } = useLocalParticipant();
  const { quality: livekitQuality } = useConnectionQualityIndicator({
    participant: localParticipant,
  });
  const networkStatus = useNetworkStatus();

  return getCombinedQuality(livekitQuality, networkStatus);
}
